import * as React from "react"
import { toast } from "sonner"
import { useHistory, useLocation } from "react-router-dom"

import { useClerkAuth } from "@/hooks/useClerkAuth"
import { useActiveBrokerAccount } from "@/hooks/useActiveBrokerAccount"
import { useRunInstrumentSetupAfterSync } from "@/hooks/useRunInstrumentSetupAfterSync"
import { useActiveBrokerCapabilities } from "@/hooks/useActiveBrokerCapabilities"
import { useResyncTrades } from "@/hooks/useResyncTrades"
import { syncTradesFromBroker } from "@/services/tradeApi"
import { afterBrokerResyncWaitForAnalytics } from "@/utils/brokerSyncStatusPoll"
import { findTradeAccount } from "@/utils/tradeAccountAnalytics"
import { shouldShowSidebarResyncTrades } from "@/utils/tradeAccountSource"
import { presentMutationError, toastError, toastWarning } from "../utils/showApiErrorToast";

const ACCOUNT_SETTINGS_RETURN_PATH_KEY = "account_settings_sidebar_return_path"

/** Resync handler shared by the shadcn sidebar footer (`AppSidebar` in `app-sidebar.tsx`). */
export function useSidebarResyncTrades() {
  const history = useHistory()
  const location = useLocation()
  const { isLoaded, user } = useClerkAuth()
  const { isResyncing, setResyncing } = useResyncTrades()
  const {
    activeAccountCode,
    activeBrokerName,
    accounts,
    refetch: refetchBrokerAccounts,
  } = useActiveBrokerAccount()
  const runInstrumentSetupAfterSync = useRunInstrumentSetupAfterSync()
  const { capabilities, loading: brokerCapabilitiesLoading } = useActiveBrokerCapabilities(
    activeAccountCode,
    activeBrokerName
  )
  const activeTradeAccount = React.useMemo(
    () => findTradeAccount(accounts, activeAccountCode, activeBrokerName),
    [accounts, activeAccountCode, activeBrokerName]
  )
  const showResync = React.useMemo(
    () =>
      shouldShowSidebarResyncTrades({
        activeAccountCode,
        activeBrokerName,
        activeAccount: activeTradeAccount,
        brokerSupportsAutoSync: capabilities?.auth?.supports_auto_sync,
        capabilitiesResolved: !brokerCapabilitiesLoading,
      }),
    [
      activeAccountCode,
      activeBrokerName,
      activeTradeAccount,
      capabilities?.auth?.supports_auto_sync,
      brokerCapabilitiesLoading,
    ]
  )

  const onResync = React.useCallback(async () => {
    if (!isLoaded || !user?.id) {
      toastError("Authentication required", {
        description: "Please log in to sync trades.",
      })
      return
    }

    const accountCode = activeAccountCode

    if (!accountCode || !activeBrokerName) {
      const currentPath = `${location.pathname}${location.search}`
      if (!currentPath.startsWith("/profile")) {
        window.sessionStorage.setItem(ACCOUNT_SETTINGS_RETURN_PATH_KEY, currentPath)
      }
      toastWarning("No synced trade account found", {
        description:
          "Please connect and sync a trade account from the Broker Management page first.",
      })
      history.push({
        pathname: "/profile/broker-api",
        state: { fromSidebarPath: currentPath },
      })
      return
    }

    setResyncing(true)
    try {
      const result = await syncTradesFromBroker(
        accountCode,
        undefined,
        undefined,
        activeBrokerName
      )
      const stats = result?.sync_stats ?? { created: 0, updated: 0, skipped: 0, total: 0 }

      const syncStatus = await afterBrokerResyncWaitForAnalytics({
        accountCode,
        brokerName: activeBrokerName,
        userId: user.id,
        action: "sync",
        refetchBrokerAccounts,
      })

      if (syncStatus.phase === "needs_instrument_setup") {
        toast.info("Instrument setup required", {
          description: "Sync completed. Resolve pending symbols before dashboard analytics can load.",
        })
        await runInstrumentSetupAfterSync(syncStatus, syncStatus.account_code || accountCode, {
          skipTradeAccountsRefetch: true,
        })
        return
      }

      const message =
        stats.created > 0 || stats.updated > 0
          ? `Synced ${stats.total} order(s): ${stats.created} trades created, ${stats.updated} trades updated, ${stats.skipped} skipped`
          : `All ${stats.total} order(s) are already up to date`

      toast.success("Re-sync successful", {
        description: `${message} (${syncStatus.closed_trades} closed, ${syncStatus.open_trades} open).`,
      })
      await runInstrumentSetupAfterSync(result, accountCode, {
        skipTradeAccountsRefetch: true,
      })
    } catch (error: unknown) {
      presentMutationError(error, "Re-sync failed");
    } finally {
      setResyncing(false)
    }
  }, [
    activeAccountCode,
    activeBrokerName,
    history,
    isLoaded,
    location.pathname,
    location.search,
    refetchBrokerAccounts,
    runInstrumentSetupAfterSync,
    setResyncing,
    user?.id,
  ])

  return { isResyncing, onResync, showResync }
}
